import type { FeatureFlag } from '@/state/useFeatureFlags';
import { useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useFeatureFlags } from '@/state/useFeatureFlags';
import { Panel } from '../../../Dashboard/ConsolePanel';

type FeatureFlagDeployedProps = {
  flag: FeatureFlag;
};

const CHIP = 'micro-label rounded-pill inline-flex w-fit border px-2 py-0.5';

export const FeatureFlagDeployed = ({ flag: initialFlag }: FeatureFlagDeployedProps) => {
  const { updateFlag } = useFeatureFlags();
  const [flag, setFlag] = useState(initialFlag);
  const [editingName, setEditingName] = useState(false);
  const [editingDesc, setEditingDesc] = useState(false);
  const [name, setName] = useState(flag.name);
  const [description, setDescription] = useState(flag.description);
  const nameRef = useRef<HTMLInputElement | null>(null);
  const descRef = useRef<HTMLTextAreaElement | null>(null);

  const handleUpdate = async (payload: Partial<FeatureFlag>) => {
    await updateFlag(flag.id, payload);
    setFlag((prev) => ({ ...prev, ...payload }));
  };

  const saveName = async () => {
    setEditingName(false);
    const trimmed = name.trim();
    if (trimmed && trimmed !== flag.name) {
      await handleUpdate({ name: trimmed });
    } else {
      setName(flag.name);
    }
  };

  const saveDescription = async () => {
    setEditingDesc(false);
    if (description !== flag.description) {
      await handleUpdate({ description });
    }
  };

  return (
    <div className="flex flex-col gap-[14px]">
      {/* The one thing allowed to sit loose on a console page: the title row. */}
      <div className="flex flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-2">
          {editingName ? (
            <Input
              ref={nameRef}
              value={name}
              onChange={(ev) => setName(ev.target.value)}
              onBlur={saveName}
              onKeyDown={(ev) => {
                if (ev.key === 'Enter') nameRef.current?.blur();
                if (ev.key === 'Escape') {
                  setName(flag.name);
                  setEditingName(false);
                }
              }}
              className="border-line bg-field text-foreground h-8 w-auto rounded-lg text-title font-medium"
              autoFocus
            />
          ) : (
            <h1
              className="text-foreground hover:text-dim m-0 cursor-pointer text-title font-medium transition-colors duration-150"
              onClick={() => setEditingName(true)}
            >
              {flag.name}
            </h1>
          )}
          <span className={`${CHIP} border-line text-dim`}>{flag.scope}</span>
          <span className={`${CHIP} border-accent-line text-accent`}>Deployed</span>
        </div>

        {editingDesc ? (
          <Textarea
            ref={descRef}
            value={description}
            onChange={(ev) => setDescription(ev.target.value)}
            onBlur={saveDescription}
            onKeyDown={(ev) => {
              if (ev.key === 'Escape') {
                setDescription(flag.description);
                setEditingDesc(false);
              }
            }}
            className="border-line bg-field text-foreground resize-none rounded-lg text-body"
            autoFocus
          />
        ) : (
          <p
            className="supporting hover:text-foreground m-0 cursor-pointer transition-colors duration-150"
            onClick={() => setEditingDesc(true)}
          >
            {flag.description || 'Add a description...'}
          </p>
        )}
      </div>

      <Panel title="Statistics" description="Branch flag deployment metrics." />
    </div>
  );
};
